// Capo advice from the song's own chords (F5.3): for each capo position, how hard are the shapes
// the player would have to play? The easiest position wins.
import type { Chord } from '../theory/chord';
import type { CapoSuggestion, DisplayOptions } from '../theory/display';
import { keyUsesFlats, noteName } from '../theory/key';
import { mod } from '../util/math';
import { voicingsFor } from './shapes';
import { difficulty, type FrettedInstrument } from './voicings';

/** Capo positions considered. Above 7 the neck gets cramped. */
const MAX_CAPO = 7;
/** Cost per capo fret: a capo is a small inconvenience, and high ones crowd the neck. */
const CAPO_COST = 0.3;
/** A capo is suggested only if it makes the shapes at least this much easier than none. */
const MIN_GAIN = 0.5;

export interface TimedChord {
  chord: Chord;
  /** How long it plays, in beats: frequent chords count more. */
  beats: number;
}

/** Average shape difficulty of the song's chords with a capo at `capo`. */
export function songDifficulty(
  chords: TimedChord[],
  o: Pick<DisplayOptions, 'transpose'>,
  instrument: FrettedInstrument,
  capo: number,
): number {
  let total = 0;
  let beats = 0;
  for (const { chord, beats: n } of chords) {
    const options = voicingsFor(instrument, chord.root + o.transpose - capo, chord.quality);
    const easiest = Math.min(...options.map(difficulty));
    total += n * (Number.isFinite(easiest) ? easiest : 10);
    beats += n;
  }
  return beats ? total / beats : 0;
}

/** The capo (0–7) whose shapes are easiest for this song, and the key the shapes are in. */
export function suggestCapoForSong(
  chords: TimedChord[],
  o: DisplayOptions,
  instrument: FrettedInstrument,
): CapoSuggestion {
  const noCapo = songDifficulty(chords, o, instrument, 0);
  let best = 0;
  let bestCost = noCapo;
  for (let capo = 1; capo <= MAX_CAPO; capo++) {
    const cost = songDifficulty(chords, o, instrument, capo) + CAPO_COST * capo;
    if (cost < bestCost - 1e-9) {
      bestCost = cost;
      best = capo;
    }
  }
  if (noCapo - bestCost < MIN_GAIN) best = 0;
  const shapeTonic = mod(o.key.tonic + o.transpose - best, 12);
  return {
    capo: best,
    shapeKey:
      noteName(shapeTonic, keyUsesFlats(shapeTonic, o.key.mode)) +
      (o.key.mode === 'minor' ? 'm' : ''),
  };
}
